import { useCallback, useEffect, useMemo, useState, RefObject } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import { addLocale, locale } from "primereact/api";

import { Timesheet } from "domain/entities/timesheet";
import { TimesheetDay } from "domain/entities/timesheet-day";
import { PurchaseOrder } from "domain/entities/purchase-order";
import { AbscenceType } from "domain/entities/abscence-type";
import { Parameter } from "domain/entities/parameter";

import { useAuthStore } from "presentation/stores/use-auth-store";
import { usePurchaseOrdersByFilter } from "presentation/hooks/use-query-purchase-order";
import { useAbscenceTypesByFilter } from "presentation/hooks/use-query-abscence-type";
import { PurchaseOrderInputFilter } from "domain/models/purchase-order-input-filter";
import { AbscenceTypeInputFilter } from "domain/models/abscence-type-input-filter";

import MobileBottomSheet from "presentation/mobile/components/MobileBottomSheet";
import { abscenceTypesInDays } from "presentation/mobile/utils/abscence-types-in-days.util";
import { isProtectedAbscence, isVacationAbscence } from "presentation/mobile/utils/protected-abscence-types.util";
import { isNoHoursPoType } from "presentation/mobile/utils/po-types.util";

import { buildMultiDayPayload, findMultiDayConflicts } from "presentation/mobile/pages/Timesheets/utils/multi-day-payload.util";
import { MultiDayFormSection } from "./components/MultiDayFormSection";
import { MultiDayCalendarSection } from "./components/MultiDayCalendarSection";

addLocale("pt", {
    firstDayOfWeek: 1,
    dayNames: ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"],
    dayNamesShort: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
    dayNamesMin: ["D", "S", "T", "Q", "Q", "S", "S"],
    monthNames: ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"],
    monthNamesShort: ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"],
    today: "Hoje",
    clear: "Limpar",
    weekHeader: "Sem",
    dateFormat: "dd/mm/yy",
});
locale("pt");

interface MultiDayEntryModalProps {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    timesheet: Timesheet;
    currentDays: TimesheetDay[];
    toastRef?: RefObject<Toast | null>;
    onSave: (payload: TimesheetDay[]) => Promise<void> | void;
}

type EntryType = "project" | "abscence";

const MultiDayEntryModal: React.FC<MultiDayEntryModalProps> = ({
    visible,
    setVisible,
    timesheet,
    currentDays,
    toastRef,
    onSave,
}) => {

    const { userEmployee, email: userEmployeeEmail } = useAuthStore();

    const [entryType, setEntryType] = useState<EntryType>("project");
    const [selectedPO, setSelectedPO] = useState<string | null>(null);
    const [selectedAbscence, setSelectedAbscence] = useState<string | null>(null);
    const [overwrite, setOverwrite] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [hours, setHours] = useState<number>(8);
    const [selectedDates, setSelectedDates] = useState<Date[]>([]);
    const [viewDate, setViewDate] = useState<Date>(() => new Date(timesheet.year, timesheet.month - 1, 1));

    useEffect(() => {
        if (visible) {
            setViewDate(new Date(timesheet.year, timesheet.month - 1, 1));
            setIsSaving(false);
        }
    }, [timesheet.year, timesheet.month, visible]);

    const { data: purchaseOrderData } = usePurchaseOrdersByFilter({
        limit: 100,
        sortBy: "name",
        sortOrder: "asc",
        employeeId: userEmployee?.id,
    } as PurchaseOrderInputFilter);

    const { data: abscenceData } = useAbscenceTypesByFilter({
        limit: 100,
        sortBy: "name",
        sortOrder: "asc",
        contractType: userEmployee?.contractType,
    } as AbscenceTypeInputFilter);

    const poOptions: Parameter[] = useMemo(
        () => (purchaseOrderData?.data ?? []).map((po: PurchaseOrder) => ({
            id: po.id,
            value: po.type?.toLowerCase() === "hora extra" ? `${po.name} (Hora Extra)` : po.name,
            type: po.type ?? "purchaseOrder",
        })),
        [purchaseOrderData]
    );

    const selectedPoObj = useMemo(() => {
        if (!selectedPO || entryType !== "project") return null;
        return purchaseOrderData?.data?.find((po: PurchaseOrder) => po.id === selectedPO) ?? null;
    }, [selectedPO, entryType, purchaseOrderData]);

    const isSelectedPOExtraHour = useMemo(() => selectedPoObj?.type?.toLowerCase() === "hora extra", [selectedPoObj]);

    const extraHourPoIds = useMemo(() =>
        new Set((purchaseOrderData?.data ?? [])
            .filter((po: PurchaseOrder) => po.type?.toLowerCase() === "hora extra" && !!po.id)
            .map((po: PurchaseOrder) => po.id as string)
        ),
        [purchaseOrderData]
    );

    const isDeslocacaoOrPrevencao = useMemo(() => isNoHoursPoType(selectedPoObj?.type), [selectedPoObj]);

    const minDate = useMemo(() => {
        const monthStart = new Date(timesheet.year, timesheet.month - 1, 1);
        if (entryType === "project" && selectedPoObj?.startAt) {
            const poStart = new Date(selectedPoObj.startAt);
            poStart.setHours(0, 0, 0, 0);
            return poStart > monthStart ? poStart : monthStart;
        }
        return monthStart;
    }, [timesheet.year, timesheet.month, entryType, selectedPoObj]);

    const maxDate = useMemo(() => {
        const monthEnd = new Date(timesheet.year, timesheet.month, 0);
        if (entryType === "project" && selectedPoObj?.endAt) {
            const poEnd = new Date(selectedPoObj.endAt);
            poEnd.setHours(23, 59, 59, 999);
            return poEnd < monthEnd ? poEnd : monthEnd;
        }
        return monthEnd;
    }, [timesheet.year, timesheet.month, entryType, selectedPoObj]);

    const abscenceOptions: Parameter[] = useMemo(
        () => (abscenceData?.data ?? [])
            .filter((ab: AbscenceType) => !isProtectedAbscence(ab.name))
            .map((ab: AbscenceType) => ({
                id: ab.id,
                value: ab.name,
                type: "abscenceType",
            })),
        [abscenceData]
    );

    const isAbscenceInDays = useMemo(() => {
        if (entryType !== "abscence" || !selectedAbscence) return false;
        const found = abscenceOptions.find((o) => o.id === selectedAbscence);
        return found ? abscenceTypesInDays.includes(found.value) : false;
    }, [entryType, selectedAbscence, abscenceOptions]);

    useEffect(() => {
        if (isAbscenceInDays) setHours(userEmployee?.workHoursPerDay ?? 8);
    }, [isAbscenceInDays, userEmployee?.workHoursPerDay]);

    useEffect(() => {
        if (entryType === "project" && selectedPoObj) {
            if (isDeslocacaoOrPrevencao) setHours(0);
            else if (hours === 0) setHours(8);
        }
    }, [selectedPoObj, entryType, isDeslocacaoOrPrevencao]);

    useEffect(() => {
        if (!visible) {
            setEntryType("project");
            setSelectedPO(null);
            setSelectedAbscence(null);
            setHours(8);
            setSelectedDates([]);
            setOverwrite(false);
            setIsSaving(false);
        }
    }, [visible]);

    const holidayDates = useMemo(() => {
        return currentDays
            .filter((d) =>
                // Colaboradores de suporte podem trabalhar em feriados; só as férias ficam bloqueadas
                userEmployee?.isSuportService
                    ? isVacationAbscence(d.abscenceType?.name)
                    : isProtectedAbscence(d.abscenceType?.name)
            )
            .map((d) => new Date(timesheet.year, timesheet.month - 1, d.day));
    }, [currentDays, timesheet.year, timesheet.month, userEmployee?.isSuportService]);

    const daysWithRecords = useMemo(
        () => currentDays.filter((d) => (d.purchaseOrders && d.purchaseOrders.length > 0) || (d.abscenceType && d.abscenceType.name !== "Feriado")).map((d) => d.day),
        [currentDays]
    );

    const canWorkWeekends = !!userEmployee?.isSuportService || isSelectedPOExtraHour;

    const isDateSelectable = useCallback(
        (date: Date): boolean => {
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;
            const isHoliday = holidayDates.some((h) => h.toDateString() === date.toDateString());
            return (canWorkWeekends || !isWeekend) && !isHoliday && date >= minDate && date <= maxDate;
        },
        [canWorkWeekends, holidayDates, minDate, maxDate]
    );

    useEffect(() => {
        setSelectedDates((prev) => {
            const allowed = prev.filter(isDateSelectable);
            return allowed.length === prev.length ? prev : allowed;
        });
    }, [isDateSelectable]);

    const allWorkingDays = useMemo(() => {
        const totalDays = new Date(timesheet.year, timesheet.month, 0).getDate();
        return Array.from(
            { length: totalDays },
            (_, index) => new Date(timesheet.year, timesheet.month - 1, index + 1)
        ).filter(isDateSelectable);
    }, [timesheet.year, timesheet.month, isDateSelectable]);

    const isSaveDisabled =
        selectedDates.length === 0 ||
        (!isDeslocacaoOrPrevencao && hours <= 0) ||
        (entryType === "project" && !selectedPO) ||
        (entryType === "abscence" && !selectedAbscence);

    const handleSave = async () => {
        if (isSaveDisabled || isSaving) return;

        const payload = buildMultiDayPayload({
            selectedDates,
            currentDays,
            entryType,
            selectedPO,
            selectedAbscence,
            purchaseOrders: purchaseOrderData?.data,
            abscenceOptions,
            hours,
            isDeslocacaoOrPrevencao,
            overwrite,
            timesheetId: timesheet.id,
            userEmployeeEmail,
        });

        // Mesmas regras do TimesheetDayForm da web; bloqueia antes de enviar
        const conflicts = findMultiDayConflicts({ payload, currentDays, entryType, overwrite, extraHourPoIds });
        if (conflicts.length > 0) {
            const daysByReason = new Map<string, number[]>();
            conflicts.forEach((c) =>
                daysByReason.set(c.reason, [...(daysByReason.get(c.reason) ?? []), c.day])
            );
            const detail = Array.from(daysByReason.entries())
                .map(([reason, days]) => `${days.length > 1 ? "Dias" : "Dia"} ${days.join(", ")}: ${reason}.`)
                .join(" ");

            toastRef?.current?.show({
                severity: "error",
                summary: "Não é possível guardar",
                detail,
                life: 6000,
            });
            return;
        }

        setIsSaving(true);
        try {
            await onSave(payload);
        } finally {
            setIsSaving(false);
        }
    };


    return (
        <MobileBottomSheet
            visible={visible}
            onHide={() => !isSaving && setVisible(false)}
            title="Registo de Vários Dias"
            closable={!isSaving}
            dismissableMask={!isSaving}
            headerIcon="pi pi-calendar"
            footer={
                <div className="w-full flex justify-content-center align-items-center gap-3">
                    <Button label="Cancelar" type="button" onClick={() => setVisible(false)} disabled={isSaving} className="btn-mobile-secondary" style={{ width: "135px", height: "42px" }} />
                    <Button icon={isSaving ? "pi pi-spin pi-spinner" : "pi pi-check"} label={isSaving ? "A guardar..." : "Guardar"} type="button" onClick={handleSave} disabled={isSaveDisabled || isSaving} className="btn-mobile" style={{ width: "135px", height: "42px" }} />
                </div>
            }
        >
            <div className="flex flex-column gap-3" aria-busy={isSaving} style={{
                pointerEvents: isSaving ? "none" : "auto",
                opacity: isSaving ? 0.6 : 1,
                transition: "opacity 0.2s ease",
            }}>
                <MultiDayFormSection
                    entryType={entryType}
                    setEntryType={setEntryType}
                    selectedPO={selectedPO}
                    setSelectedPO={setSelectedPO}
                    poOptions={poOptions}
                    isDeslocacaoOrPrevencao={isDeslocacaoOrPrevencao}
                    selectedAbscence={selectedAbscence}
                    setSelectedAbscence={setSelectedAbscence}
                    abscenceOptions={abscenceOptions}
                    hours={hours}
                    setHours={setHours}
                    isAbscenceInDays={isAbscenceInDays}
                />

                <div
                    className="flex align-items-start gap-3 border-1 border-round-lg p-3"
                    style={{
                        borderColor: overwrite ? "#FDE68A" : "#E2E8F0",
                        backgroundColor: overwrite ? "#FFFBEB" : "#FFFFFF",
                    }}
                >
                    <Checkbox
                        inputId="overwrite-records"
                        checked={overwrite}
                        onChange={(e) => setOverwrite(e.checked ?? false)}
                    />
                    <label htmlFor="overwrite-records" className="flex flex-column cursor-pointer">
                        <span className="text-sm font-medium" style={{ color: "#1E293B" }}>
                            Sobrescrever registos existentes
                        </span>
                        <span className="text-xs mt-1" style={{ color: overwrite ? "#B45309" : "#64748B" }}>
                            {overwrite
                                ? "Os registos que já existem nos dias escolhidos serão substituídos."
                                : "O novo registo é adicionado aos que já existem nos dias escolhidos."}
                        </span>
                    </label>
                </div>
            </div>

            <MultiDayCalendarSection
                selectedDates={selectedDates}
                setSelectedDates={setSelectedDates}
                viewDate={viewDate}
                setViewDate={setViewDate}
                minDate={minDate}
                maxDate={maxDate}
                disabledDays={canWorkWeekends ? [] : [0, 6] }
                holidayDates={holidayDates}
                daysWithRecords={daysWithRecords}
                onSelectAll={() => setSelectedDates(allWorkingDays)}
                onClearSelection={() => setSelectedDates([])}
            />

        </MobileBottomSheet >
    );
};

export default MultiDayEntryModal;